<template>
  <div class="inputs-container mt-3 mb-3">
    <input
      v-for="(digit, index) in digits"
      :key="index"
      type="text"
      inputmode="text"
      maxlength="1"
      autocomplete="off"
      autocorrect="off"
      spellcheck="false"
      class="input-digit text-center text-2xl border rounded focus:outline-none"
      :class="{
        highlighted: highlighted(index),
        disabled: !!props.errorMessage,
      }"
      v-model="digits[index]"
      @focus="onFocusTextInput(index, $event)"
      @blur="focusedIndex = null"
      @input="handleInput(index, $event)"
      @paste="handlePaste(index, $event)"
      @keydown="handleKeydown(index, $event)"
      :id="`${props.componentId || 'lastFourDigitsValidationId'}${index}`"
      :data-resource-id="`${
        props.componentId || 'lastFourDigitsValidationId'
      }${index}`"
    />
  </div>
  <div v-if="props.errorMessage" class="error-message">
    {{ props.errorMessage }}
  </div>
  <div v-if="props.warningMessage" class="warning-message">
    {{ props.warningMessage }}
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";

interface Props {
  errorMessage?: string;
  warningMessage?: string;
  componentId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  errorMessage: "",
  warningMessage: "",
  componentId: "",
});

const emit = defineEmits<{
  (e: "focusin", event: FocusEvent): void;
  (e: "validateDigits", value: string): void;
  (e: "submit"): void;
}>();

const digits = ref<string[]>(["", "", "", ""]);
const focusedIndex = ref<number | null>(null);
const fullCode = computed(() => digits.value.join(""));

const highlighted = (index: number) => {
  if (focusedIndex.value === index) return true;
  if (digits.value[index]) return true;
  if (index < digits.value.length - 1 && digits.value[index + 1]) return true;
  return false;
};

const handleInput = (index: number, event: Event) => {
  const target = event.target as HTMLInputElement;
  let value = target.value;

  // Force only one character - take the first character only
  value = value.slice(0, 1);

  // Remove non-alphanumeric characters and convert to uppercase
  value = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

  // Update the reactive data
  digits.value[index] = value;

  // Update the input field to reflect the restricted value
  target.value = value;

  // Automatically move to the next input if a valid character is entered
  if (value && index < digits.value.length - 1) {
    const nextInput = target.nextElementSibling as HTMLInputElement | null;
    if (nextInput) {
      nextInput.focus();
    }
  }
};

const handlePaste = (index: number, event: ClipboardEvent) => {
  event.preventDefault(); // Prevent the default paste behavior

  const pastedData = event.clipboardData?.getData("text") || "";
  const cleanData = pastedData.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

  // Only take the first character from the pasted data
  if (cleanData.length > 0) {
    digits.value[index] = cleanData[0];

    // Update the actual input element
    const target = event.target as HTMLInputElement;
    target.value = cleanData[0];

    // Move focus to the next input if available
    if (index < digits.value.length - 1) {
      const nextInput = target.nextElementSibling as HTMLInputElement | null;
      if (nextInput) {
        nextInput.focus();
      }
    }
  }
};

const onFocusTextInput = (index: number, event: FocusEvent) => {
  focusedIndex.value = index;
  emit("focusin", event);
};

const handleKeydown = (index: number, event: KeyboardEvent) => {
  if (event.key === "Backspace") {
    if (digits.value[index] === "" && index > 0) {
      event.preventDefault();
      digits.value[index - 1] = "";
      const prevInput = (event.target as HTMLInputElement)
        .previousElementSibling as HTMLInputElement | null;
      if (prevInput) {
        prevInput.value = "";
        prevInput.focus();
      }
    }
  } else if (event.key === "ArrowLeft" && index > 0) {
    event.preventDefault();
    const prevInput = (event.target as HTMLInputElement)
      .previousElementSibling as HTMLInputElement | null;
    if (prevInput) prevInput.focus();
  } else if (event.key === "ArrowRight" && index < digits.value.length - 1) {
    event.preventDefault();
    const nextInput = (event.target as HTMLInputElement)
      .nextElementSibling as HTMLInputElement | null;
    if (nextInput) nextInput.focus();
  } else if (
    event.key === "Enter" &&
    index === digits.value.length - 1 &&
    digits.value.every((d) => d)
  ) {
    emit("submit");
  }
};

watch(fullCode, (newVal) => {
  if (newVal.length === 4 && !digits.value.includes("")) {
    emit("validateDigits", newVal);
  } else {
    emit("validateDigits", "");
  }
});

watch(
  () => props.errorMessage,
  (newVal) => {
    if (newVal) {
      digits.value = ["", "", "", ""];
      focusedIndex.value = 0;
    }
  }
);
</script>

<style scoped lang="scss">
@import "@/assets/scss/_constants.scss";

.inputs-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  width: 100%;
}
.input-digit {
  width: 65px;
  height: 65px;
  font-size: 32px;
  text-align: center;
  border: 1px solid #d1d1d1;
  border-radius: 5px;
  font-family: Inter;
  font-weight: 600;
  color: #595a5a;
  background-color: #fff;
  transition: border 0.2s;
}

.input-digit:focus,
.input-digit.highlighted {
  border: 1.5px solid #aed3ad !important;
  background-color: #fff !important;
  outline: none !important;
  box-shadow: none !important;
}

.input-digit.disabled {
  background-color: #fff !important;
  cursor: not-allowed;
  border: 1.5px solid $pj-color-red !important;
}

.error-message {
  color: $pj-color-red;
  font-size: 13px;
  line-height: 17px;
  font-weight: 400;
  font-family: Inter;
}

.warning-message {
  color: #595a5a;
  font-size: 13px;
  line-height: 17px;
  font-weight: 600;
  font-family: Inter;
  width: 100%;
  margin-left: 0;
  align-self: flex-start;
}
</style>
